/**
 * The Axiom scope as the settings page sees it: a set of checked projects and
 * collections.
 *
 * The stored value keeps the shape the server's parseAxiomScope
 * (src/learning/sources/axiom-source.js) reads — so a scope typed by hand
 * before the picker existed still shows up checked, and whatever the picker
 * saves is read by chat exactly as it was meant.
 */

/**
 * Read a stored scope into `{ projectIds, collectionIds }`.
 *
 * Accepts every shape parseAxiomScope accepts: a JSON object with
 * `projectIds`/`collectionIds` (or the singular forms), a JSON array of ids or
 * `{ projectId }`/`{ collectionId }` entries, or bare ids separated by commas
 * or whitespace, read as projects.
 *
 * @param {string|null|undefined} raw
 */
export function parseScopeSelection(raw) {
  const projectIds = [];
  const collectionIds = [];
  const add = (list, ids) => {
    for (const id of Array.isArray(ids) ? ids : [ids]) {
      if (typeof id === 'string' && id.trim() && !list.includes(id.trim())) list.push(id.trim());
    }
  };

  const text = typeof raw === 'string' ? raw.trim() : '';
  if (!text) return { projectIds, collectionIds };

  let value;
  try {
    value = JSON.parse(text);
  } catch {
    add(projectIds, text.split(/[\s,]+/));
    return { projectIds, collectionIds };
  }

  if (Array.isArray(value)) {
    for (const entry of value) {
      if (typeof entry === 'string') add(projectIds, entry);
      else if (entry?.projectId) add(projectIds, entry.projectId);
      else if (entry?.collectionId) add(collectionIds, entry.collectionId);
    }
  } else if (value && typeof value === 'object') {
    add(projectIds, value.projectIds ?? []);
    add(projectIds, value.projectId ?? []);
    add(collectionIds, value.collectionIds ?? []);
    add(collectionIds, value.collectionId ?? []);
  }
  return { projectIds, collectionIds };
}

/**
 * The value to store for a selection, or null when nothing is checked.
 *
 * @param {{ projectIds: string[], collectionIds: string[] }} selection
 */
export function serializeScopeSelection({ projectIds = [], collectionIds = [] }) {
  if (projectIds.length === 0 && collectionIds.length === 0) return null;
  return JSON.stringify({ projectIds, collectionIds });
}

/** Whether two selections check the same targets, order aside. */
export function sameScopeSelection(a, b) {
  const same = (x = [], y = []) => x.length === y.length && x.every((id) => y.includes(id));
  return same(a.projectIds, b.projectIds) && same(a.collectionIds, b.collectionIds);
}

/**
 * Ids in the stored scope that the token no longer reaches — a deleted
 * collection, or a scope typed for a different token. Shown so the user can
 * see why a checked-looking scope finds nothing.
 *
 * @param {{ projectIds: string[], collectionIds: string[] }} selection
 * @param {{ projects: Array<{id: string}>, collections: Array<{id: string}> }} targets
 */
export function unknownScopeIds(selection, targets) {
  const projectIds = new Set((targets.projects || []).map((p) => p.id));
  const collectionIds = new Set((targets.collections || []).map((c) => c.id));
  return [
    ...selection.projectIds.filter((id) => !projectIds.has(id)),
    ...selection.collectionIds.filter((id) => !collectionIds.has(id)),
  ];
}

/**
 * The selection limited to what the token reaches. An old plain-string scope
 * parses as project ids even when it names a collection; such an id moves to
 * the collections. Ids the token no longer reaches are dropped: they have no
 * checkbox, so keeping them would save the warning back with every save.
 */
export function reconcileScopeSelection(selection, targets) {
  if (!targets) return selection;
  const projectIds = new Set((targets.projects || []).map((p) => p.id));
  const collectionIds = new Set((targets.collections || []).map((c) => c.id));
  const all = [...selection.projectIds, ...selection.collectionIds];
  const unique = (ids) => [...new Set(ids)];
  return {
    projectIds: unique(all.filter((id) => projectIds.has(id))),
    collectionIds: unique(all.filter((id) => collectionIds.has(id) && !projectIds.has(id))),
  };
}
